import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import performRequest from "network/perform-request";
import {
  competitionsShowRoute,
  schedulingClearRoute,
  schedulingConfigRoute,
  schedulingPreparationUpdateRoute,
  schedulingPreviewRoute,
  schedulingRunRoute,
  schedulingSaveConfigRoute,
  schedulingWindowsDeleteRoute,
  schedulingWindowsSaveRoute,
} from "network/api-routes";
import { canApplyDraft } from "./CompetitionSchedulingParts/schedulingDraft";
import { MIDNIGHT_END_TIME } from "./CompetitionSchedulingParts/helpers";

const DEFAULT_CONFIG = {
  default_match_duration_minutes: 75,
  semifinal_match_duration_minutes: 90,
  final_match_duration_minutes: 90,
  min_rest_minutes: 120,
  semifinal_final_rest_minutes: null,
  buffer_between_matches_minutes: 15,
  max_matches_per_day_per_player: null,
  weekday_match_limits: [],
  date_match_limits: [],
  player_unavailability: [],
  resource_preferences: [],
  club_transfers: [],
  ignore_operating_hours: false,
  ignore_bookings: false,
};

const EMPTY_WINDOW_FORM = {
  establishment_id: "",
  field_id: "",
  dates: [],
  dateInput: "",
  start_time: "",
  end_time: "",
  ends_at_midnight: false,
};

export default function useCompetitionScheduling(competitionUuid) {
  const { id: paramId } = useParams();
  const id = competitionUuid || paramId;
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [preparation, setPreparation] = useState(null);
  const [refreshingPreparation, setRefreshingPreparation] = useState(false);
  const [updatingPreparation, setUpdatingPreparation] = useState(false);
  const [selectedFinalId, setSelectedFinalId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [schedulingParticipants, setSchedulingParticipants] = useState([]);
  const [configSaved, setConfigSaved] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);
  const [windows, setWindows] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [fields, setFields] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [windowModalOpen, setWindowModalOpen] = useState(false);
  const [windowForm, setWindowForm] = useState(EMPTY_WINDOW_FORM);
  const [previewResult, setPreviewResult] = useState(null);
  const [incidentPins, setIncidentPins] = useState([]);
  const [incidentError, setIncidentError] = useState("");
  const [anchorConflicts, setAnchorConflicts] = useState([]);
  const [proposalConflicts, setProposalConflicts] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  const [confirmRunOpen, setConfirmRunOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const fetchConfig = useCallback(() => {
    setLoading(true);
    performRequest("POST", schedulingConfigRoute, { competition_uuid: id })
      .then((res) => {
        if (res.data.config) {
          setConfig({
            ...DEFAULT_CONFIG,
            ...res.data.config,
            max_matches_per_day_per_player: res.data.config.max_matches_per_day_per_player ?? null,
          });
          setConfigSaved(true);
          setConfigDirty(false);
        } else {
          setConfig(DEFAULT_CONFIG);
          setConfigSaved(false);
          setConfigDirty(false);
        }
        setWindows(res.data.windows || []);
        setSchedulingParticipants(res.data.scheduling_participants || []);
        setPreparation(res.data.preparation || null);
        setFields(res.data.fields || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  const fetchCompetition = useCallback(() => {
    performRequest("POST", competitionsShowRoute, { competition_uuid: id })
      .then((res) => {
        const competition = res.data.data;
        setEstablishments(competition.establishments?.data || []);
        setTournaments(competition.tournaments?.data || []);
      })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    fetchConfig();
    fetchCompetition();
  }, [fetchConfig, fetchCompetition]);

  const canSchedule = configSaved && !configDirty && windows.length > 0 && !updatingPreparation;

  const refreshPreparation = async () => {
    setRefreshingPreparation(true);
    try {
      const response = await performRequest("POST", schedulingConfigRoute, { competition_uuid: id });
      setPreparation(response.data.preparation || null);
      return Boolean(response.data.preparation);
    } catch {
      setPreparation(null);
      enqueueSnackbar(intl.formatMessage({ id: "scheduling.preparation.unavailable" }), { variant: "warning" });
      return false;
    } finally {
      setRefreshingPreparation(false);
    }
  };

  const updatePreparation = async (payload) => {
    setUpdatingPreparation(true);
    try {
      const response = await performRequest("POST", schedulingPreparationUpdateRoute, {
        competition_uuid: id,
        ...payload,
      });
      setPreparation(response.data.preparation);
      setPreviewResult(null);
      fetchCompetition();
      enqueueSnackbar(
        intl.formatMessage(
          { id: "scheduling.bulk.saved" },
          { count: response.data.changed_tournament_ids.length },
        ),
        { variant: "success" },
      );
      return true;
    } catch (error) {
      const detail = Object.values(error.response?.data?.errors || {}).flat()[0] || error.response?.data?.message;
      enqueueSnackbar(detail || intl.formatMessage({ id: "scheduling.bulk.error" }), { variant: "error" });
      if (error.response?.status === 409) await refreshPreparation();
      return false;
    } finally {
      setUpdatingPreparation(false);
    }
  };

  const windowsByDate = useMemo(() => {
    const grouped = {};
    [...windows]
      .sort((a, b) => a.date.localeCompare(b.date))
      .forEach((windowItem) => {
        if (!grouped[windowItem.date]) grouped[windowItem.date] = [];
        grouped[windowItem.date].push(windowItem);
      });
    return grouped;
  }, [windows]);
  const fieldsForEstablishment = fields.filter(
    (field) => field.establishment_id === parseInt(windowForm.establishment_id),
  );
  const availabilityEstablishments = useMemo(() => {
    const compatibleClubIds = new Set(fields.map((field) => String(field.establishment_id)));
    return establishments.filter((establishment) => compatibleClubIds.has(String(establishment.id)));
  }, [establishments, fields]);

  const handleConfigChange = (field) => (event) => {
    setPreviewResult(null);
    setConfig((previous) => ({ ...previous, [field]: parseInt(event.target.value) || 0 }));
    setConfigDirty(true);
  };

  const handleNullableConfigChange = (field) => (event) => {
    setPreviewResult(null);
    const nextValue = event.target.value;
    setConfig((previous) => ({
      ...previous,
      [field]: nextValue === "" ? null : parseInt(nextValue, 10),
    }));
    setConfigDirty(true);
  };

  const handleToggleChange = (field) => (event) => {
    setPreviewResult(null);
    setConfig((previous) => ({ ...previous, [field]: event.target.checked }));
    setConfigDirty(true);
  };

  const handleDailyRulesChange = (field, rules) => {
    setPreviewResult(null);
    setConfig((previous) => ({ ...previous, [field]: rules }));
    setConfigDirty(true);
  };

  const saveConfig = () => {
    setSaving(true);
    performRequest("POST", schedulingSaveConfigRoute, { competition_uuid: id, ...config })
      .then((res) => {
        setConfig(res.data.config);
        setConfigSaved(true);
        setConfigDirty(false);
        enqueueSnackbar(intl.formatMessage({ id: "Configuración guardada" }), { variant: "success" });
        setSaving(false);
      })
      .catch((error) => {
        const validationError = Object.values(error.response?.data?.errors || {}).flat()[0];
        enqueueSnackbar(
          validationError || intl.formatMessage({ id: "Error al guardar configuración" }),
          { variant: "error" },
        );
        setSaving(false);
      });
  };

  const discardConfigChanges = () => fetchConfig();
  const editWindow = (windowItem) => {
    const end = windowItem.end_time.slice(0, 5);
    setWindowForm({
      window_id: windowItem.id,
      establishment_id: windowItem.establishment_id,
      field_id: windowItem.field_id || "",
      dates: [windowItem.date.slice(0, 10)],
      dateInput: "",
      start_time: windowItem.start_time.slice(0, 5),
      end_time: end === "24:00" ? "00:00" : end,
      ends_at_midnight: end === "24:00" || end === "00:00",
    });
    setWindowModalOpen(true);
  };

  const openWindowModal = () => {
    setWindowForm({
      ...EMPTY_WINDOW_FORM,
      establishment_id: availabilityEstablishments.length > 0 ? availabilityEstablishments[0].id : "",
    });
    setWindowModalOpen(true);
  };
  const handleWindowFormChange = (field) => (event) => {
    const nextValue = event.target.value;
    setWindowForm((previous) => ({
      ...previous,
      [field]: nextValue,
      ...(field === "establishment_id" ? { field_id: "" } : {}),
    }));
  };
  const handleEndTimeChange = (event) => {
    const nextValue = event.target.value;
    setWindowForm((previous) => ({
      ...previous,
      end_time: nextValue,
      ends_at_midnight: nextValue === "00:00",
    }));
  };
  const handleEndsAtMidnightChange = (event) => {
    const checked = event.target.checked;
    setWindowForm((previous) => ({
      ...previous,
      ends_at_midnight: checked,
      end_time: checked ? "00:00" : previous.end_time === "00:00" ? "" : previous.end_time,
    }));
  };
  const addDateToForm = () => {
    if (windowForm.window_id && windowForm.dates.length > 0) return;
    if (windowForm.dateInput && !windowForm.dates.includes(windowForm.dateInput)) {
      setWindowForm((previous) => ({
        ...previous,
        dates: [...previous.dates, previous.dateInput].sort(),
        dateInput: "",
      }));
    }
  };
  const removeDateFromForm = (dateToRemove) => {
    setWindowForm((previous) => ({
      ...previous,
      dates: previous.dates.filter((date) => date !== dateToRemove),
    }));
  };
  const windowTimeRangeInvalid =
    !!windowForm.start_time &&
    !!windowForm.end_time &&
    !windowForm.ends_at_midnight &&
    windowForm.start_time >= windowForm.end_time;

  const saveWindow = () => {
    if (windowTimeRangeInvalid) {
      enqueueSnackbar(intl.formatMessage({ id: "La hora de fin debe ser posterior a la de inicio" }), {
        variant: "warning",
      });
      return;
    }
    setSaving(true);
    const params = {
      competition_uuid: id,
      ...(windowForm.window_id ? { window_id: windowForm.window_id } : {}),
      establishment_id: windowForm.establishment_id,
      dates: windowForm.dates,
      start_time: windowForm.start_time,
      end_time: windowForm.ends_at_midnight ? MIDNIGHT_END_TIME : windowForm.end_time,
    };
    if (windowForm.field_id) params.field_id = windowForm.field_id;
    performRequest("POST", schedulingWindowsSaveRoute, params)
      .then(() => {
        setPreviewResult(null);
        const count = windowForm.dates.length;
        enqueueSnackbar(
          count > 1
            ? intl.formatMessage({ id: "Ventanas creadas para {count} fechas" }, { count })
            : intl.formatMessage({ id: "Ventana de disponibilidad guardada" }),
          { variant: "success" },
        );
        setWindowModalOpen(false);
        setSaving(false);
        fetchConfig();
      })
      .catch(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Error al guardar ventana" }), { variant: "error" });
        setSaving(false);
      });
  };

  const deleteWindow = (windowId) => {
    performRequest("POST", schedulingWindowsDeleteRoute, { competition_uuid: id, window_id: windowId })
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Ventana eliminada" }), { variant: "success" });
        setPreviewResult(null);
        fetchConfig();
      })
      .catch(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Error al eliminar ventana" }), { variant: "error" });
      });
  };

  const runPreview = (incident = null) => {
    if (!incident?.establishment_id) incident = null;
    setProposalConflicts([]);
    setAnchorConflicts([]);
    setScheduling(true);
    setPreviewResult(null);
    setIncidentError("");
    performRequest("POST", incident ? "/scheduling/incident/preview" : schedulingPreviewRoute, {
      competition_uuid: id,
      ...(incident || {}),
    })
      .then((res) => {
        setPreviewResult(res.data);
        setIncidentPins([]);
        setScheduling(false);
      })
      .catch((error) => {
        if (incident) {
          setIncidentPins(error.response?.data?.incident_pinned_matches || []);
          setIncidentError(
            Object.values(error.response?.data?.errors || {}).flat().join(" ") ||
              error.response?.data?.message ||
              intl.formatMessage({ id: "Error al generar vista previa" }),
          );
        }
        setAnchorConflicts(error.response?.data?.anchor_conflicts || []);
        setProposalConflicts(error.response?.data?.proposal_conflicts || []);
        enqueueSnackbar(intl.formatMessage({ id: "Error al generar vista previa" }), {
          variant: "error",
        });
        setScheduling(false);
      });
  };

  const runScheduling = (acknowledgeRestEquity = false) => {
    if (!canApplyDraft(previewResult)) {
      enqueueSnackbar(intl.formatMessage({ id: "scheduling.draft.required" }), {
        variant: "warning",
      });
      return;
    }
    setProposalConflicts([]);
    setAnchorConflicts([]);
    setConfirmRunOpen(false);
    setScheduling(true);
    performRequest("POST", schedulingRunRoute, {
      competition_uuid: id,
      draft_id: previewResult.draft.id,
      ...(acknowledgeRestEquity ? { acknowledge_rest_equity: true } : {}),
    })
      .then((res) => {
        setPreviewResult(res.data.already_applied ? null : res.data);
        enqueueSnackbar(res.data.message || intl.formatMessage({ id: "Calendarización ejecutada" }), {
          variant: res.data.already_applied || res.data.unscheduled_count === 0 ? "success" : "warning",
        });
        setScheduling(false);
      })
      .catch((error) => {
        setAnchorConflicts(error.response?.data?.anchor_conflicts || []);
        setProposalConflicts(error.response?.data?.proposal_conflicts || []);
        if (error.response?.status === 409) setPreviewResult(null);
        enqueueSnackbar(
          error?.response?.data?.errors?.schedule?.[0] ||
            error?.response?.data?.message ||
            intl.formatMessage({ id: "Error al ejecutar calendarización" }),
          { variant: "error" },
        );
        setScheduling(false);
      });
  };

  const clearScheduling = () => {
    setConfirmClearOpen(false);
    setScheduling(true);
    performRequest("POST", schedulingClearRoute, { competition_uuid: id })
      .then((res) => {
        enqueueSnackbar(res.data.message || intl.formatMessage({ id: "Calendarización limpiada" }), {
          variant: "success",
        });
        setPreviewResult(null);
        setScheduling(false);
      })
      .catch(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Error al limpiar calendarización" }), {
          variant: "error",
        });
        setScheduling(false);
      });
  };

  const handleIncidentChange = (keepPins) => {
    setPreviewResult(null);
    setIncidentError("");
    if (!keepPins) setIncidentPins([]);
  };
  const handleFinalUpdated = () => {
    setSelectedFinalId(null);
    setPreviewResult(null);
    refreshPreparation();
  };

  return {
    id,
    intl,
    loading,
    preparation,
    refreshingPreparation,
    updatingPreparation,
    selectedFinalId,
    setSelectedFinalId,
    saving,
    config,
    schedulingParticipants,
    configSaved,
    configDirty,
    windows,
    establishments,
    availabilityEstablishments,
    fields,
    tournaments,
    windowModalOpen,
    setWindowModalOpen,
    windowForm,
    previewResult,
    incidentPins,
    incidentError,
    anchorConflicts,
    proposalConflicts,
    scheduling,
    confirmRunOpen,
    setConfirmRunOpen,
    confirmClearOpen,
    setConfirmClearOpen,
    canSchedule,
    windowsByDate,
    fieldsForEstablishment,
    windowTimeRangeInvalid,
    refreshPreparation,
    updatePreparation,
    handleConfigChange,
    handleNullableConfigChange,
    handleToggleChange,
    handleDailyRulesChange,
    saveConfig,
    discardConfigChanges,
    editWindow,
    openWindowModal,
    handleWindowFormChange,
    handleEndTimeChange,
    handleEndsAtMidnightChange,
    addDateToForm,
    removeDateFromForm,
    saveWindow,
    deleteWindow,
    runPreview,
    runScheduling,
    clearScheduling,
    handleIncidentChange,
    handleFinalUpdated,
  };
}
